import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Card,
  CardContent,
  Avatar,
  Collapse,
  IconButton,
  Switch,
  FormControlLabel,
  Link,
} from '@mui/material';
import {
  Add as AddIcon,
  ExpandLess,
  ExpandMore,
  Topic as TopicIcon,
  Label as LabelIcon,
  Description as DescriptionIcon,
  Language as LanguageIcon,
  ImageOutlined as ImageOutlinedIcon,
  LockOutlined as LockOutlinedIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PlatformSelection from './PlatformSelection';
import GeneratedContent, { ScrollToTopFab } from './GeneratedContent';
import LoadingSpinner from '../../components/LoadingSpinner';

// Flag component using flagcdn.com
const Flag = ({ countryCode, size = 20 }: { countryCode: string; size?: number }) => (
  <img
    src={`https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`}
    srcSet={`https://flagcdn.com/w80/${countryCode.toLowerCase()}.png 2x`}
    width={size}
    height={Math.round(size * 0.75)}
    alt={countryCode}
    style={{ borderRadius: 2, objectFit: 'cover' }}
  />
);

// Map language codes to country codes for flags
const getFlagCode = (languageCode: string): string => {
  const flagMap: { [key: string]: string } = {
    'en': 'GB', // English -> Great Britain
    'es': 'ES', // Spanish -> Spain
    'fr': 'FR', // French -> France
    'de': 'DE', // German -> Germany
    'zh': 'CN', // Chinese -> China
    'ar': 'SA', // Arabic -> Saudi Arabia
    'hi': 'IN', // Hindi -> India
  };
  return flagMap[languageCode] || 'UN';
};

// Character limits from backend validation
const TOPIC_MAX_LENGTH = 100;
const KEYWORD_MAX_LENGTH = 20;
const KEYWORDS_MAX_COUNT = 5;
const ADDITIONAL_CONTEXT_MAX_LENGTH = 1000;

interface GenerationFormProps {
  formData: any;
  setFormData: (data: any) => void;
  keywordInput: string;
  setKeywordInput: (input: string) => void;
  businessProfiles: any[];
  contentPurposes: any[];
  languages: any[];
  user: any;
  hasSocialMediaAccess: boolean;
  fieldErrors: { [key: string]: boolean };
  onSelectChange: (event: any) => void;
  onKeywordAdd: () => void;
  onKeywordRemove: (keyword: string) => void;
  onGenerate: () => void;
  isGenerating: boolean;
  generatedContent: any;
  copiedStates: { [key: string]: boolean };
  showScrollToTop: boolean;
  onCopy: (text: string, key: string) => void;
  onRegenerate: () => void;
  onScrollToTop: () => void;
  generateImage: boolean;
  onGenerateImageChange: (value: boolean) => void;
  imageGenerationEnabled: boolean;
}

const GenerationForm: React.FC<GenerationFormProps> = ({
  formData,
  setFormData,
  keywordInput,
  setKeywordInput,
  businessProfiles,
  contentPurposes,
  languages,
  user,
  hasSocialMediaAccess,
  fieldErrors,
  onSelectChange,
  onKeywordAdd,
  onKeywordRemove,
  onGenerate,
  isGenerating,
  generatedContent,
  copiedStates,
  showScrollToTop,
  onCopy,
  onRegenerate,
  onScrollToTop,
  generateImage,
  onGenerateImageChange,
  imageGenerationEnabled,
}) => {
  const { t } = useTranslation('content');
  const [isFormExpanded, setIsFormExpanded] = useState(true);
  const [isAdditionalInfoExpanded, setIsAdditionalInfoExpanded] = useState(true);

  // Collapse form when generating actually starts (after validation passes)
  useEffect(() => {
    if (isGenerating) {
      setIsFormExpanded(false);
    }
  }, [isGenerating]);

  // Expand form when generation completes (success or error)
  useEffect(() => {
    if (!isGenerating && generatedContent) {
      // Keep collapsed when content is generated (user can expand manually)
      // But if there's an error, we might want to expand - but let's keep it collapsed for now
    }
  }, [isGenerating, generatedContent]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleGenerate = () => {
    // Don't collapse here - let validation happen first
    // Collapse will happen automatically when isGenerating becomes true
    onGenerate();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Alert 
        severity="info" 
        sx={{ borderRadius: 2 }}
      >
        {t('generation.patienceMessage')}
      </Alert>
      {/* Form Card - Collapsible */}
      <Card sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <CardContent>
          {/* Form Header - Entire header is clickable */}
          <Box 
            onClick={() => setIsFormExpanded(!isFormExpanded)}
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 2,
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8,
              },
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('generation.formTitle')}
            </Typography>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setIsFormExpanded(!isFormExpanded);
              }}
              size="small"
            >
              {isFormExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>

          {/* Collapsible Form Content */}
          <Collapse in={isFormExpanded}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Business Profile Selection */}
      {user?.role === 'client' ? (
        // For regular users, show selected business profile without card
        businessProfiles && businessProfiles.length > 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Avatar sx={{ backgroundColor: 'primary.main', width: 48, height: 48 }}>
              {businessProfiles[0].business_name.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {businessProfiles[0].business_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {typeof businessProfiles[0].business_type === 'object' 
                  ? businessProfiles[0].business_type?.display_name || businessProfiles[0].business_type?.name || 'Unknown'
                  : businessProfiles[0].business_type || 'Unknown'
                }
              </Typography>
            </Box>
          </Box>
        ) : (
          <Alert severity="warning">
            {t('generation.noProfileWarning')}
          </Alert>
        )
      ) : (
        // For admin/agency users, show dropdown
        <FormControl fullWidth required error={fieldErrors.business_profile}>
          <InputLabel sx={{ color: fieldErrors.business_profile ? 'error.main' : 'inherit' }}>
            {t('generation.businessProfile')} *
          </InputLabel>
          <Select
            name="business_profile_id"
            value={formData.business_profile_id}
            onChange={onSelectChange}
            label={`${t('generation.businessProfile')} *`}
            sx={{ 
              borderRadius: 2,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: fieldErrors.business_profile ? 'error.main' : 'inherit',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: fieldErrors.business_profile ? 'error.main' : 'inherit',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: fieldErrors.business_profile ? 'error.main' : 'primary.main',
              }
            }}
          >
            {businessProfiles?.map((profile) => (
              <MenuItem key={profile.id} value={profile.id}>
                {profile.business_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      
      {/* Content Purpose and Language - Same Row */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {/* Content Purpose */}
        <FormControl 
          sx={{ flex: 1, minWidth: 200 }}
          required 
          error={fieldErrors.content_purpose}
        >
          <InputLabel 
            error={fieldErrors.content_purpose}
          >
            {t('generation.selectPurpose')}
          </InputLabel>
          <Select
            name="content_purpose"
            value={formData.content_purpose}
            onChange={onSelectChange}
            label={t('generation.selectPurpose')}
            sx={{ 
              borderRadius: 2,
            }}
          >
            {contentPurposes?.map((purpose) => {
              // Convert purpose name to translation key (e.g., "Product Launch" -> "product_launch")
              const translationKey = purpose.name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
              // Try to get translated name, fallback to display_name from API
              const translatedName = t(`purposes.${translationKey}`, { defaultValue: purpose.display_name });
              return (
                <MenuItem key={purpose.id} value={purpose.name}>
                  {translatedName}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        {/* Language Selection */}
        <FormControl 
          sx={{ flex: 1, minWidth: 180 }}
        >
          <InputLabel>
            {t('generation.selectLanguage')}
          </InputLabel>
          <Select
            name="language"
            value={formData.language || ''}
            onChange={onSelectChange}
            label={t('generation.selectLanguage')}
            sx={{ 
              borderRadius: 2,
            }}
            renderValue={(value) => {
              const selectedLang = languages?.find((l: any) => l.code === value);
              if (!selectedLang) return value;
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Flag countryCode={getFlagCode(selectedLang.code)} size={18} />
                  <span>{selectedLang.native_name || selectedLang.name}</span>
                </Box>
              );
            }}
          >
            {languages?.map((lang: any) => (
              <MenuItem key={lang.code} value={lang.code}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Flag countryCode={getFlagCode(lang.code)} size={18} />
                  <span>{lang.native_name || lang.name}</span>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      {/* Optional Fields Card */}
      <Card sx={{ 
        border: '1px solid', 
        borderColor: 'divider', 
        borderRadius: 2,
        backgroundColor: 'grey.50',
      }}>
        <CardContent sx={{ '&:last-child': { pb: 3 } }}>
          {/* Header with Collapse Button - Entire header is clickable */}
          <Box 
            onClick={() => setIsAdditionalInfoExpanded(!isAdditionalInfoExpanded)}
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 2,
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8,
              },
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {t('generation.additionalInfoTitle')}
            </Typography>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setIsAdditionalInfoExpanded(!isAdditionalInfoExpanded);
              }}
              size="small"
            >
              {isAdditionalInfoExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>

          {/* Collapsible Content */}
          <Collapse in={isAdditionalInfoExpanded}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Topic */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <TopicIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {t('generation.topic')}
                </Typography>
              </Box>
              <TextField
                fullWidth
                name="topic"
                value={formData.topic}
                onChange={handleChange}
                placeholder={t('generation.topicPlaceholder')}
                helperText={t('generation.topicHelperText', { current: formData.topic.length, max: TOPIC_MAX_LENGTH })}
                inputProps={{ maxLength: TOPIC_MAX_LENGTH }}
                sx={{ 
                  borderRadius: 2,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                  },
                }}
              />
            </Box>
            
            {/* Keywords */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <LabelIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {t('generation.keywords')}
                </Typography>
              </Box>
              <Box sx={{ 
                display: 'flex', 
                gap: 1.5, 
                mb: 1,
                flexWrap: 'wrap',
                alignItems: 'flex-start',
              }}>
                <TextField
                  size="small"
                  placeholder={t('generation.keywordsPlaceholder')}
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), onKeywordAdd())}
                  helperText={t('generation.keywordsHelperText', { 
                    current: keywordInput.length, 
                    max: KEYWORD_MAX_LENGTH,
                    count: formData.keywords.length,
                    maxCount: KEYWORDS_MAX_COUNT
                  })}
                  inputProps={{ maxLength: KEYWORD_MAX_LENGTH }}
                  sx={{ 
                    flexGrow: 1,
                    minWidth: 200,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                    },
                  }}
                />
                <Button
                  onClick={onKeywordAdd}
                  startIcon={<AddIcon />}
                  variant="outlined"
                  size="small"
                  disabled={!keywordInput.trim() || formData.keywords.length >= KEYWORDS_MAX_COUNT}
                  sx={{
                    flexShrink: 0,
                    height: '40px', // Match MUI small TextField input height
                    alignSelf: 'flex-start',
                    mt: 0, // No top margin to align with input
                  }}
                >
                  {t('generation.addKeyword')}
                </Button>
              </Box>
              {formData.keywords.length > 0 && (
                <Box sx={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 1,
                  p: 1.5,
                  backgroundColor: 'white',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  minHeight: 48,
                }}>
                  {formData.keywords.map((keyword: string) => (
                    <Chip
                      key={keyword}
                      label={keyword}
                      onDelete={() => onKeywordRemove(keyword)}
                      color="primary"
                      variant="filled"
                      sx={{ 
                        borderRadius: 2,
                        backgroundColor: '#6C63FF',
                        color: 'white',
                        '& .MuiChip-deleteIcon': {
                          color: 'white',
                          '&:hover': {
                            color: 'rgba(255, 255, 255, 0.8)',
                          },
                        },
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
            
            {/* Additional Context */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <DescriptionIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {t('generation.additionalContext')}
                </Typography>
              </Box>
              <TextField
                fullWidth
                name="additional_context"
                value={formData.additional_context}
                onChange={handleChange}
                multiline
                rows={3}
                placeholder={t('generation.additionalContextPlaceholder')}
                helperText={t('generation.additionalContextHelperText', { 
                  current: formData.additional_context.length, 
                  max: ADDITIONAL_CONTEXT_MAX_LENGTH 
                })}
                inputProps={{ maxLength: ADDITIONAL_CONTEXT_MAX_LENGTH }}
                sx={{ 
                  borderRadius: 2,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                  },
                }}
              />
            </Box>
          </Box>
          </Collapse>
        </CardContent>
      </Card>
      
      {/* AI Image Generation Toggle */}
      <Card sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        backgroundColor: 'grey.50',
      }}>
        <CardContent sx={{ '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: imageGenerationEnabled ? 1.5 : 0 }}>
            <ImageOutlinedIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {t('generation.image.title')}
            </Typography>
            {!imageGenerationEnabled && (
              <Chip
                label="Pro"
                size="small"
                sx={{ ml: 0.5, height: 18, fontSize: '0.65rem', bgcolor: 'primary.main', color: 'white' }}
              />
            )}
          </Box>

          {imageGenerationEnabled ? (
            <FormControlLabel
              control={
                <Switch
                  checked={generateImage}
                  onChange={(e) => onGenerateImageChange(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {t('generation.image.toggleLabel')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('generation.image.toggleHelp')}
                  </Typography>
                </Box>
              }
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <LockOutlinedIcon sx={{ fontSize: 18, color: 'text.disabled', mt: 0.3 }} />
              <Box>
                <Typography variant="body2" color="text.disabled">
                  {t('generation.image.proOnly')}
                </Typography>
                <Link component={RouterLink} to="/billing" variant="body2" sx={{ mt: 0.5, display: 'inline-block' }}>
                  {t('generation.image.upgrade')}
                </Link>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Platform Selection */}
      <PlatformSelection
        formData={formData}
        setFormData={setFormData}
        hasSocialMediaAccess={hasSocialMediaAccess}
        error={fieldErrors.platforms}
      />
      
              {/* Generate Button */}
              <Box sx={{ mt: 'auto', pt: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleGenerate}
                  disabled={isGenerating || (hasSocialMediaAccess && (!formData.platforms || formData.platforms.length === 0))}
                  sx={{ 
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                  }}
                >
                  {isGenerating
                    ? (generateImage ? t('generation.generatingWithImage') : t('generation.generating'))
                    : t('generation.generate')}
                </Button>
              </Box>
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* Loading Spinner */}
      {isGenerating && (
        <Card sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <CardContent>
            <LoadingSpinner message={generateImage ? t('generation.generatingContentWithImage') : t('generation.generatingContent')} />
          </CardContent>
        </Card>
      )}

      {/* Generated Content Section */}
      {generatedContent && !isGenerating && (
        <GeneratedContent
          generatedContent={generatedContent}
          copiedStates={copiedStates}
          showScrollToTop={showScrollToTop}
          onCopy={onCopy}
          onRegenerate={onRegenerate}
          onScrollToTop={onScrollToTop}
        />
      )}

      {/* Scroll to Top FAB */}
      <ScrollToTopFab show={showScrollToTop} onClick={onScrollToTop} />
    </Box>
  );
};

export default GenerationForm;
